// Every football incident type LSports can send (see the provider's
// "Football – Incident ID / Incident Name / Kind" list), classified for the
// MatchTracker pitch:
//   key    – i18n key of the singular incident name shown on the pitch
//   alias  – same real-world event as another type (e.g. a header goal is a
//            goal): treated as that type, so it's never announced twice
//   ignore – not a pitch incident: player-level stats, percentages, xG,
//            timers, status/meta records, possession (read from extra data),
//            score (read from the scoreboard), passes (far too frequent)
// `track: false` = an event type we react to only when the feed names it as
// CurrentIncident, not when its statistic goes up.
export const FOOTBALL_INCIDENTS = {
    1: {key: 'incidents.corner'},
    2: {key: 'incidents.shotontarget'},
    3: {key: 'incidents.shotofftarget'},
    4: {key: 'incidents.attack'},
    5: {key: 'incidents.dangerousattack'},
    6: {key: 'incidents.yellowcard'},
    7: {key: 'incidents.redcard'},
    8: {key: 'incidents.penalty'},
    9: {key: 'incidents.goal'},
    10: {key: 'incidents.substitution'},
    11: {key: 'incidents.possession', track: false}, // Possession: also derived from LivescoreExtraData
    12: {key: 'incidents.foul'},
    13: {key: 'incidents.freekick'},
    14: {key: 'incidents.goalkick'},
    15: {key: 'incidents.offside'},
    16: {key: 'incidents.blockedshot'},
    17: {key: 'incidents.throwin'},
    18: {key: 'incidents.woodwork'},
    24: {key: 'incidents.owngoal'},
    25: {key: 'incidents.penaltygoal', track: false}, // not in the list, kept for older feeds
    27: {ignore: true},                      // Score (scoreboard)
    40: {key: 'incidents.missedpenalty'},
    88: {ignore: true},                      // Clearances: too minor for the pitch
    96: {key: 'incidents.save'},
    102: {ignore: true},
    148: {ignore: true},
    149: {ignore: true},
    1028: {ignore: true},
    1030: {ignore: true},
    1036: {ignore: true},
    1074: {key: 'incidents.var'},
    2001: {ignore: true},
    2003: {ignore: true},
    2004: {alias: 96},                       // PlayerSaves (team stat too) = save
    2005: {ignore: true},
    2006: {ignore: true},
    2007: {ignore: true},
    2034: {ignore: true},                    // Assists: part of the goal
    2039: {ignore: true},
    2046: {key: 'incidents.shot'},
    2115: {ignore: true},                    // Interceptions: too minor for the pitch
    2148: {ignore: true},                    // Crosses: too minor for the pitch
    2151: {ignore: true},
    2152: {ignore: true},
    2153: {ignore: true},
    2154: {ignore: true},
    2155: {ignore: true},
    2158: {key: 'incidents.injury'},
    2173: {ignore: true},
    2226: {ignore: true},
    2227: {ignore: true},
    2228: {ignore: true},                    // Tackles: too minor for the pitch
    2255: {ignore: true},
    2260: {ignore: true},                    // Team xG
    2277: {ignore: true},                    // Period (scoreboard)
    2278: {ignore: true},                    // FixtureStatus (scoreboard)
    2279: {ignore: true},                    // Timer (scoreboard)
    2280: {ignore: true},
    2288: {ignore: true},                    // Tackles (raw)
    2289: {alias: 9},                        // Header goal
    2318: {alias: 9},                        // Left foot goal
    2319: {alias: 9},                        // Right foot goal
    2320: {alias: 25},                       // Penalty goal
    2325: {ignore: true},
    2327: {ignore: true},
    2330: {ignore: true},
    2344: {alias: 18},                       // Woodwork (raw)
    2370: {ignore: true},
    2371: {ignore: true},
    2372: {ignore: true},
    2373: {ignore: true},
    2374: {alias: 9},                        // Goal from outside the box
    2376: {ignore: true},
    2388: {alias: 9},                        // Free-kick goal
    2389: {key: 'incidents.penaltysaved'},
    2394: {key: 'incidents.dangerousfreekick'},
    2399: {ignore: true},
    2423: {breakTime: true, ignore: true},   // BreakTime: drives the half-time phase
    2428: {alias: 2046},                     // Shot inside the box
    2429: {alias: 2046},                     // Shot outside the box
    2430: {ignore: true},                    // Successful dribbles: too minor for the pitch
    2438: {key: 'incidents.dangergeneral'},
    2439: {key: 'incidents.dangercards'},
    2440: {key: 'incidents.dangercorners'},
    2442: {ignore: true},
    2462: {ignore: true},                    // StatusDescription
    2474: {key: 'incidents.medicalteam'},
    2490: {ignore: true},
    2491: {ignore: true},
    2616: {ignore: true},                    // CurrentIncident record itself
    2619: {ignore: true}                     // ExtraData record
};

export const BREAK_TIME_TYPE = 2423;

// { type, ignored, known, breakTime } for a raw provider type.
export function resolveFootballType(rawType) {
    const t = parseInt(rawType) || 0;
    const def = FOOTBALL_INCIDENTS[t];
    if (!def) return {type: t, ignored: false, known: false, breakTime: false};
    if (def.alias) return {type: def.alias, ignored: false, known: true, breakTime: false};
    return {type: t, ignored: !!def.ignore, known: true, breakTime: !!def.breakTime};
}

// Team statistics whose increase is read as "this just happened". Aliases
// aren't tracked (their main type already is), nor ignored/feed-only types.
export const FOOTBALL_STATS_TO_TRACK = Object.keys(FOOTBALL_INCIDENTS)
    .map(Number)
    .filter(t => {
        const def = FOOTBALL_INCIDENTS[t];
        return def.key && def.track !== false;
    });

export function footballIncidentKey(type) {
    const def = FOOTBALL_INCIDENTS[type];
    return def && def.key ? def.key : null;
}
